'use client';

import React, { useEffect, useLayoutEffect, useState } from 'react';
import Markdown from 'react-markdown';
import { useConfig } from '@/app/contexts/ConfigContext';
import Loader from '@/components/Loader';
import { scrollPageToTop } from '@/helpers';

export default function PrivacyPolicy() {
  const { config, selectedLanguage } = useConfig();
  const [markdownContent, setMarkdownContent] = useState('');
  const [hasLangLoaded, setHasLangLoaded] = useState(false);

  useLayoutEffect(() => {
    scrollPageToTop();
  }, []);

  useEffect(() => {
    if (markdownContent) {
      scrollPageToTop();
    }
  }, [markdownContent]);

  // Used to delay the md loading until the language is loaded
  useEffect(() => {
    if (selectedLanguage) {
      setHasLangLoaded(true);
    }
  }, [selectedLanguage]);

  // Load the markdown file based on the config language
  useEffect(() => {
    const loadMarkdown = async () => {
      try {
        const legalParams = new URLSearchParams({
          serviceUrlLink: config.SERVICE_URL_LINK,
          language: selectedLanguage,
          fileName: 'privacy',
        });

        if (config.FLOW_TYPE) {
          legalParams.set('flowType', config.FLOW_TYPE);
        }

        if (config.LEGALS_DYNAMIC_S3_PATH_FROM_SERVICE_URL) {
          legalParams.set('legalsDynamicS3PathFromServiceUrl', 'true');
        }

        const response = await fetch(
          config.PRIVACY_URL
            ? `api/legals-custom-url?url=${config.PRIVACY_URL}`
            : `api/legals?${legalParams.toString()}`
        );
        if (!response.ok) {
          throw new Error(`[Privacy] Could not load file: privacy.md`);
        }

        const { fileContents } = await response.json();

        if (fileContents) {
          // Use variables from config
          const text = fileContents
            .replace(/COMPANY_NAME/g, config.COMPANY_NAME)
            .replace(/COMPANY_ADDRESS/g, config.COMPANY_ADDRESS)
            .replace(
              /REGISTRATION_NUMBER/g,
              config.REGISTRATION_NUMBER
            )
            .replace(/SERVICE_NAME/g, config.SERVICE_NAME)
            .replace(
              /SERVICE_CONTACT_EMAIL/g,
              config.SERVICE_CONTACT_EMAIL
            )
            .replace(/SERVICE_URL_LINK/g, () => {
              const url = config.SERVICE_URL_LINK;
              return url.startsWith('http') ? url : `https://${url}`;
            });

          setMarkdownContent(text);
        }
      } catch (error) {
        console.error('[Privacy] Error loading Markdown file:', error);
        setMarkdownContent('Error loading content.');
      }
    };

    if (config && selectedLanguage && hasLangLoaded) loadMarkdown();
  }, [config, selectedLanguage, hasLangLoaded]);

  return (
    <main className='max-w-[1920px] mx-auto overflow-hidden'>
      {!markdownContent && <Loader />}
      {markdownContent && (
        <div className='min-h-screen sm:p-8 px-4'>
          <div className='max-w-4xl mx-auto py-4'>
            <Markdown
              className='prose prose-lg'
              components={{
                h1: ({ node, ...props }) => (
                  <h1
                    className='text-3xl font-bold my-6'
                    style={{ color: config.theme.textColorDark }}
                    {...props}
                  />
                ),
                h2: ({ node, ...props }) => (
                  <h2
                    className='text-2xl font-semibold mt-4 mb-2'
                    style={{ color: config.theme.textColorDark }}
                    {...props}
                  />
                ),
                p: ({ node, ...props }) => (
                  <p
                    className='mb-4'
                    style={{ color: config.theme.textColorDark }}
                    {...props}
                  />
                ),
                ul: ({ node, ...props }) => (
                  <ul
                    className='list-disc pl-6 mb-4'
                    style={{ color: config.theme.textColorDark }}
                    {...props}
                  />
                ),
                a: ({ href, ...props }) => (
                  <a
                    href={href}
                    className='text-blue-600'
                    style={{ color: config.theme.accentColorOne }}
                    {...props}
                  />
                ),
              }}
            >
              {markdownContent}
            </Markdown>
          </div>
        </div>
      )}
    </main>
  );
}
